import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, ApiError } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import type { Topic, TopicSubscription } from '../types';

// ---------------------------------------------------------------------------
// Active topic taxonomy (~32 rows, changes rarely → aggressive cache).
// ---------------------------------------------------------------------------

export function useTopicsQuery() {
  return useQuery<Topic[]>({
    queryKey: queryKeys.topics.list,
    queryFn: async () => {
      const result = await apiClient.get<{ data: Topic[] }>('/topics');
      return result.data.data;
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Current user's topic subscriptions.
// ---------------------------------------------------------------------------

export function useTopicSubscriptionsQuery(options?: { readonly enabled?: boolean }) {
  return useQuery<TopicSubscription[]>({
    queryKey: queryKeys.topicSubscriptions.list,
    queryFn: async () => {
      const result = await apiClient.get<{ data: TopicSubscription[] }>('/topic-subscriptions');
      return result.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useSubscribeTopicMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (topicSlug: string) => {
      try {
        await apiClient.post('/topic-subscriptions', { topicSlug });
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 409) return;
        throw err;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.topicSubscriptions.all });
    },
  });
}

export function useUnsubscribeTopicMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (topicSlug: string) => {
      await apiClient.delete(`/topic-subscriptions/${encodeURIComponent(topicSlug)}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.topicSubscriptions.all });
    },
  });
}
